"use client";

import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { Section } from "@/components/ui/Section";
import { Marquee } from "@/components/ui/Marquee";
import { TechIcon } from "@/components/icons/TechIcons";

// --- Data Configuration ---

interface TechItem {
  key: string;
  name: string;
  slug: string;
  type: "core" | "tool";
}

// Mapped from user list + available local icons
const coreStack: TechItem[] = [
  { key: "php", name: "PHP", slug: "php", type: "core" },
  { key: "laravel", name: "Laravel", slug: "laravel", type: "core" },
  { key: "symfony", name: "Symfony", slug: "symfony", type: "core" },
  { key: "python", name: "Python", slug: "python", type: "core" },
  { key: "fastapi", name: "FastAPI", slug: "fastapi", type: "core" },
  { key: "flask", name: "Flask", slug: "flask", type: "core" },
  { key: "csharp", name: "C#", slug: "csharp", type: "core" },
  { key: "lua", name: "Lua", slug: "lua", type: "core" },
  { key: "kotlin", name: "Kotlin", slug: "kotlin", type: "core" },
  { key: "flutter", name: "Flutter", slug: "flutter", type: "core" },
  { key: "dart", name: "Dart", slug: "dart", type: "core" },
  { key: "pwa", name: "Progressive Web Apps", slug: "pwa", type: "core" },
  { key: "node", name: "Node.js", slug: "nodejs", type: "core" },
  { key: "express", name: "Express.js", slug: "expressjs", type: "core" },
  { key: "nest", name: "NestJS", slug: "nestjs", type: "core" },
  { key: "fastify", name: "Fastify", slug: "fastify", type: "core" },
  { key: "js", name: "JavaScript", slug: "javascript", type: "core" },
  { key: "ts", name: "TypeScript", slug: "typescript", type: "core" },
  { key: "react", name: "React", slug: "react", type: "core" },
  { key: "next", name: "Next.js", slug: "nextjs", type: "core" },
  { key: "vue", name: "Vue.js", slug: "vue", type: "core" },
  { key: "nuxt", name: "Nuxt", slug: "nuxt", type: "core" },
  { key: "angular", name: "Angular", slug: "angular", type: "core" },
  { key: "ember", name: "Ember.js", slug: "ember", type: "core" },
  { key: "html", name: "HTML5", slug: "html5", type: "core" },
  { key: "css", name: "CSS3", slug: "css3", type: "core" },
  { key: "sass", name: "Sass", slug: "sass", type: "core" },
  { key: "tailwind", name: "Tailwind", slug: "tailwindcss", type: "core" },
  { key: "mysql", name: "MySQL", slug: "mysql", type: "core" },
  { key: "postgres", name: "PostgreSQL", slug: "postgresql", type: "core" },
  { key: "mongo", name: "MongoDB", slug: "mongodb", type: "core" },
  { key: "redis", name: "Redis", slug: "redis", type: "core" },
];

const toolsStack: TechItem[] = [
  { key: "git", name: "Git", slug: "git", type: "tool" },
  { key: "github", name: "GitHub", slug: "github", type: "tool" },
  { key: "gitlab", name: "GitLab", slug: "gitlab", type: "tool" },
  { key: "bitbucket", name: "Bitbucket", slug: "bitbucket", type: "tool" },
  { key: "linux", name: "Linux", slug: "linux", type: "tool" },
  { key: "docker", name: "Docker", slug: "docker", type: "tool" },
  { key: "cicd", name: "CI/CD", slug: "githubactions", type: "tool" },
  { key: "aws", name: "Amazon Web Services", slug: "aws", type: "tool" },
  { key: "gcp", name: "Google Cloud Platform", slug: "gcp", type: "tool" },
  { key: "cloudflare", name: "Cloudflare", slug: "cloudflare", type: "tool" },
  { key: "firebase", name: "Firebase", slug: "firebase", type: "tool" },
  { key: "figma", name: "Figma", slug: "figma", type: "tool" },
  { key: "photoshop", name: "Photoshop", slug: "photoshop", type: "tool" },
  {
    key: "illustrator",
    name: "Illustrator",
    slug: "illustrator",
    type: "tool",
  },
  { key: "vscode", name: "Visual Studio Code", slug: "vscode", type: "tool" },
  { key: "atom", name: "Atom", slug: "atom", type: "tool" },
  { key: "pycharm", name: "PyCharm", slug: "pycharm", type: "tool" },
  {
    key: "androidstudio",
    name: "Android Studio",
    slug: "androidstudio",
    type: "tool",
  },
  { key: "arduino", name: "Arduino", slug: "arduino", type: "tool" },
  {
    key: "analytics",
    name: "Google Analytics",
    slug: "googleanalytics",
    type: "tool",
  },
  { key: "jira", name: "Jira", slug: "jira", type: "tool" },
];

export function Skills() {
  const t = useTranslations("Index");
  const tSkills = useTranslations("Skills");

  return (
    <Section
      id="skills"
      title={t("skills_title")}
      subtitle={t("skills_subtitle")}
      className="bg-(--bg-card) border-y border-(--border-subtle)"
    >
      {/* Lanes Wrapper */}
      <div className="flex flex-col pt-4">
        {/* Lane 1: Core Stack (Left->Right, Slower, Larger) */}
        <div className="relative w-full">
          {/* Fade masks as requested (screenshot look) */}
          <div className="absolute left-0 top-0 bottom-0 w-32 z-20 bg-linear-to-r from-(--bg-card) to-transparent pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-32 z-20 bg-linear-to-l from-(--bg-card) to-transparent pointer-events-none" />

          {/* Tighter gap (gap-4 = 1rem) for packed look */}
          <Marquee
            velocity={0.3} // Very Slow, Left->Right
            pauseOnHover
            className="py-4 [--gap:1rem] overflow-x-clip overflow-y-visible"
          >
            {coreStack.map((tech) => (
              <SkillCard key={tech.key} tech={tech} tooltipDirection="top" />
            ))}
          </Marquee>
        </div>

        {/* Lane 2: Tools (Right->Left, Slightly Faster) */}
        <div className="relative w-full">
          {/* Fade masks */}
          <div className="absolute left-0 top-0 bottom-0 w-32 z-20 bg-linear-to-r from-(--bg-card) to-transparent pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-32 z-20 bg-linear-to-l from-(--bg-card) to-transparent pointer-events-none" />

          <Marquee
            velocity={-0.4} // Slow but slightly faster, Right->Left
            pauseOnHover
            className="py-4 [--gap:1rem] overflow-x-clip overflow-y-visible"
          >
            {toolsStack.map((tech) => (
              <SkillCard key={tech.key} tech={tech} tooltipDirection="bottom" />
            ))}
          </Marquee>
        </div>
      </div>

      {/* Professional Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4, duration: 0.8 }}
        className="mt-16 max-w-4xl mx-auto text-center px-6 relative z-10"
      >
        <p className="text-lg md:text-xl text-(--text-secondary) font-light leading-relaxed tracking-wide">
          {tSkills("summary")}
        </p>
      </motion.div>
    </Section>
  );
}

function SkillCard({
  tech,
  tooltipDirection = "top",
}: {
  tech: TechItem;
  tooltipDirection?: "top" | "bottom";
}) {
  return (
    <motion.div
      layout
      className="group relative flex flex-col shrink-0 w-24 h-24 rounded-2xl bg-(--bg-primary)/50 hover:bg-(--bg-primary)/90 border border-(--border-subtle) transition-all duration-300"
    >
      {/* Background Icon Overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.04] dark:opacity-[0.06] pointer-events-none z-0">
        <div className="w-18 h-18">
          <TechIcon name={tech.slug} />
        </div>
      </div>

      {/* Tooltip */}
      <div
        className={`absolute left-1/2 -translate-x-1/2 px-4 py-1 rounded-xl bg-(--bg-primary)/90 border border-(--border-subtle) backdrop-blur-md whitespace-nowrap z-10 pointer-events-none opacity-0 group-hover:opacity-100 transition-all duration-300 ${
          tooltipDirection === "top" ? "bottom-full mb-3" : "top-full mt-3"
        }`}
      >
        <span className="text-sm font-medium text-(--text-primary)">
          {tech.name}
        </span>
      </div>

      {/* Inner Container */}
      <div className="relative flex-1 flex flex-col items-center justify-center p-4">
        {/* Icon Centered - Colored by default as per screenshot */}
        <div className="relative z-20 w-10 h-10">
          <TechIcon name={tech.slug} />
        </div>
      </div>
    </motion.div>
  );
}
